import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGameStore } from '../stores/gameStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { ONBOARDING_STEPS, useOnboardingStepIndex } from '../hooks/useOnboarding';
import { sfx } from '../services/sfx';

// First-run coaching: a small, skippable hint card that walks a brand-new player
// through the core loop — buy a tool, run it, hack the entry node, take a mission.
//
// Deliberately NON-blocking: it never covers the game with a modal and never intercepts
// clicks. Each step completes by DETECTING the player's real progress (see
// hooks/useOnboarding), so the player can act in any order, and the tutorial cannot get
// stuck. Shown on every platform (itch / CrazyGames / direct).
export default function Onboarding() {
  const player = useGameStore(s => s.player);
  const { status, decide, reachStep, finish } = useOnboardingStore();
  const idx = useOnboardingStepIndex();
  const navigate = useNavigate();
  const location = useLocation();

  // Decide ONCE PER ACCOUNT whether this player gets the tutorial: a brand-new account
  // is level 1, owns nothing and has no missions in flight. Keying on the player id
  // matters — a fresh account (e.g. a new CrazyGames guest) on a browser that already
  // hosted an experienced one must still get the tutorial.
  useEffect(() => {
    if (!player?.id) return;
    const isNew =
      player.level <= 1 &&
      (player.activeMissions?.length ?? 0) === 0 &&
      (player.software?.length ?? 0) === 0;
    decide(player.id, isNew);
  }, [player, decide]);

  // Remember the furthest step reached, and close the tutorial after the last one.
  useEffect(() => {
    if (status !== 'active') return;
    if (idx >= ONBOARDING_STEPS.length) { sfx.success(); finish(); return; }
    reachStep(idx);
  }, [idx, status, reachStep, finish]);

  if (status !== 'active' || !player) return null;
  if (location.pathname === '/banned') return null;
  if (idx >= ONBOARDING_STEPS.length) return null;

  const step = ONBOARDING_STEPS[idx];
  const onRoute = location.pathname === step.route;

  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '1rem',
        zIndex: 900,           // above the page, below modals (1000)
        width: 'min(560px, calc(100vw - 1.5rem))',
        background: '#0b1220',
        border: '1px solid var(--primary-color)',
        borderRadius: '8px',
        boxShadow: '0 8px 28px rgba(0, 0, 0, 0.55)',
        padding: '0.7rem 0.85rem',
        fontFamily: 'monospace',
      }}
      className="fx-fade-in"
      role="status"
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{
          background: 'var(--primary-color)', color: '#000', borderRadius: '3px',
          padding: '0 0.4rem', fontSize: '0.7rem', fontWeight: 'bold',
        }}>
          STEP {idx + 1}/{ONBOARDING_STEPS.length}
        </span>
        <strong style={{ color: 'var(--primary-color)', fontSize: '0.88rem' }}>{step.title}</strong>
      </div>

      <p style={{ margin: '0.4rem 0 0.6rem', color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.45 }}>
        {step.body}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        {!onRoute && (
          <button
            onClick={() => { sfx.click(); navigate(step.route); }}
            className="primary"
            style={{ fontSize: '0.78rem', padding: '0.3rem 0.8rem' }}
          >
            {step.cta}
          </button>
        )}

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '0.28rem', marginLeft: onRoute ? 0 : 'auto' }}>
          {ONBOARDING_STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                width: '7px', height: '7px', borderRadius: '50%',
                background: i < idx ? 'var(--primary-color)' : i === idx ? '#fbbf24' : '#334155',
              }}
            />
          ))}
        </div>

        <button
          onClick={() => { sfx.click(); finish(); }}
          style={{
            marginLeft: onRoute ? 'auto' : 0,
            background: 'transparent', border: 'none', color: '#64748b',
            fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'monospace',
            textDecoration: 'underline',
          }}
        >
          Skip tutorial
        </button>
      </div>
    </div>
  );
}
